import qs from 'qs';
import { getAxiosInstance } from './dataService';

const getHeaders = (ssId, apiToken = '') => {
	if (ssId) {
		return {
			'X-SS-ID': ssId,
			'Content-Type': 'application/json',
		};
	}

	return {
		Authorization: `Bearer ${apiToken}`,
		'Content-Type': 'application/json',
	};
};

/**
 * Authenticate user
 * @param {string} apiEnvironment
 * @param {object} data
 * @param {object} headers
 * @returns {Promise<object>}
 * @endpoint /authorizationcode
 */
export const authenticate = async (apiEnvironment, data, headers) => {
	const result = (
		await getAxiosInstance(apiEnvironment).post(
			'/authorizationcode',
			qs.stringify(data), // this endpoint requires sending data in application/x-www-form-urlencoded format instead of JSON format, that is why using the qs library to encode data
			{
				headers,
			}
		)
	).data;
	return result;
};

/**
 * Fetch password rules
 * @param {object} queryKey
 * @returns {Promise<object>}
 * @endpoint /api/accounts/passwordRules
 */
export const fetchPasswordRules = async ({ queryKey }) => {
	const [, apiEnvironment] = queryKey;

	return (
		await getAxiosInstance(apiEnvironment).get('/api/accounts/passwordRules')
	).data;
};

/**
 * Validate password
 * @param {string} apiEnvironment
 * @param {object} data
 * @returns {Promise<object>}
 * @endpoint /api/accounts/PasswordValidation
 */
export const validatePassword = async (apiEnvironment, data) =>
	getAxiosInstance(apiEnvironment).post(
		'/api/accounts/PasswordValidation',
		data
	);

/**
 * Submit enforce password
 * @param {string} apiEnvironment
 * @param {object} data
 * @returns {Promise<object>}
 * @endpoint /api/Enforce/Password
 */
export const submitEnforcePassword = async (apiEnvironment, data) =>
	getAxiosInstance(apiEnvironment).put('/api/Enforce/Password', data);

/**
 * Submit change password
 * @param {string} apiEnv
 * @param {string} ssId
 * @param {object} data
 * @returns {Promise<object>}
 * @endpoint /api/V3/accounts/changePassword
 */
export const submitChangePassword = async (apiEnv, ssId, data) =>
	getAxiosInstance(apiEnv).post('/api/V3/accounts/changePassword', data, {
		headers: getHeaders(ssId),
	});

/**
 * Submit forgot username
 * @param {string} apiEnv
 * @param {object} data
 * @returns {Promise<object>}
 * @endpoint /api/forgotUsername
 */
export const submitForgotUsername = async (apiEnv, data) =>
	getAxiosInstance(apiEnv).post('/api/forgotUsername', data, {});

/**
 * Fetch security questions
 * @param {object} queryKey
 * @returns {Promise<object>}
 * @endpoint /api/V2/accounts/securityQuestions
 */
export const fetchSecurityQuestions = async ({ queryKey }) => {
	const [, apiEnvironment] = queryKey;

	return (
		await getAxiosInstance(apiEnvironment).get(
			'/api/V2/accounts/securityQuestions'
		)
	).data;
};

/**
 * Fetch reset password question
 * @param {object} queryKey
 * @returns {Promise<object>}
 * @endpoint /api/forgotPassword/question
 */
export const fetchResetPasswordQuestion = async ({ queryKey }) => {
	const [, apiEnvironment, token] = queryKey;
	const result = (
		await getAxiosInstance(apiEnvironment).get('/api/forgotPassword/question', {
			headers: {
				AshToken: `${token}`,
			},
		})
	).data;

	return result;
};

/**
 * Submit forgot password
 * @param {string} apiEnv
 * @param {object} data
 * @returns {Promise<object>}
 * @endpoint /api/forgotPassword
 */
export const submitForgotPassword = async (apiEnv, data) =>
	getAxiosInstance(apiEnv).post('/api/forgotPassword', data);

/**
 * Submit step one reset password
 * @param {string} apiEnvironment
 * @param {string} token
 * @param {object} data
 * @returns {Promise<object>}
 * @endpoint api/forgotPassword/verify
 */
export const submitStepOneResetPassword = async (apiEnvironment, token, data) =>
	getAxiosInstance(apiEnvironment).post('api/forgotPassword/verify', data, {
		headers: {
			AshToken: `${token}`,
		},
	});

/**
 * Submit step two reset password
 * @param {string} apiEnvironment
 * @param {string} token
 * @param {object} data
 * @returns {Promise<object>}
 * @endpoint /api/forgotPassword/confirm
 */
export const submitStepTwoResetPassword = async (apiEnvironment, token, data) =>
	getAxiosInstance(apiEnvironment).post('/api/forgotPassword/confirm', data, {
		headers: {
			AshToken: `${token}`,
		},
	});

/**
 * Submit enforce security question
 * @param {string} apiEnvironment
 * @param {object} data
 * @returns {Promise<object>}
 * @endpoint /api/Enforce/SecurityQuestion
 */
export const submitEnforceSecurityQuestion = async (apiEnvironment, data) =>
	getAxiosInstance(apiEnvironment).put('/api/Enforce/SecurityQuestion', data);

/**
 * User verification
 * @param {string} apiEnvironment
 * @param {object} data
 * @param {string} ssId
 * @returns {Promise<object>}
 * @endpoint /api/userVerification
 */
export const userVerification = async (apiEnvironment, data, ssId) =>
	getAxiosInstance(apiEnvironment).post('/api/userVerification', data, {
		headers: getHeaders(ssId),
	});

/**
 * Fetch username rules
 * @param {object} queryKey
 * @returns {Promise<object>}
 * @endpoint /api/accounts/usernameValidationRules
 */
export const fetchUsernameRules = async ({ queryKey }) => {
	const [, apiEnvironment] = queryKey;

	return (
		await getAxiosInstance(apiEnvironment).get(
			'/api/accounts/usernameValidationRules'
		)
	).data;
};

/**
 * Validate username
 * @param {string} apiEnvironment
 * @param {object} data
 * @returns {Promise<object>}
 * @endpoint /api/accounts/usernamevalidation
 */
export const validateUsername = async (apiEnvironment, data) =>
	getAxiosInstance(apiEnvironment).post(
		'/api/accounts/usernamevalidation',
		data
	);

/**
 * Submit registration step 2
 * @param {string} apiEnvironment
 * @param {object} data
 * @returns {Promise<object>}
 * @endpoint /api/UserRegistration
 */
export const submitRegistrationStep2 = async (apiEnvironment, data) =>
	getAxiosInstance(apiEnvironment).post('/api/UserRegistration', data);

/**
 * Fetch email verification status
 * @param {object} queryKey
 * @returns {Promise<object>}
 * @endpoint /api/Email/Status
 */
export const fetchEmailVerificationStatus = async ({ queryKey }) => {
	const [, apiEnvironment, ssId, apiToken] = queryKey;

	return (
		await getAxiosInstance(apiEnvironment).get('/api/Email/Status', {
			headers: getHeaders(ssId, apiToken),
		})
	).data;
};

/**
 * Fetch email from account
 * @param {object} queryKey
 * @returns {Promise<object>}
 * @endpoint /api/accounts/email
 */
export const fetchEmailFromAccount = async ({ queryKey }) => {
	const [, apiEnvironment, ssId, apiToken] = queryKey;

	return (
		await getAxiosInstance(apiEnvironment).get('/api/accounts/email', {
			headers: getHeaders(ssId, apiToken),
		})
	).data;
};

/**
 * Submit change email
 * @param {string} apiEnvironment
 * @param {string} ssId
 * @param {object} data
 * @returns {Promise<object>}
 * @endpoint /api/accounts/email
 */
export const submitChangeEmail = async (apiEnvironment, ssId, data) =>
	getAxiosInstance(apiEnvironment).put('/api/accounts/email', data, {
		headers: getHeaders(ssId),
	});

/**
 * Fetch MFA settings
 * @param {object} queryKey
 * @returns {Promise<object>}
 * @endpoint /api/Mfa/Settings
 */
export const fetchMFASettings = async ({ queryKey }) => {
	const [, apiEnvironment, ssId, apiToken] = queryKey;

	return (
		await getAxiosInstance(apiEnvironment).get('/api/Mfa/Settings', {
			headers: getHeaders(ssId, apiToken),
		})
	).data;
};

/**
 * Resend verification email
 * @param {string} apiEnvironment
 * @param {string} ssId
 * @param {string} apiToken
 * @param {string} memberId
 * @returns {Promise<object>}
 * @endpoint /api/Email/Resend
 */
export const resendVerificationEmail = async (
	apiEnvironment,
	ssId,
	apiToken,
	memberId
) => {
	const url = memberId
		? `/api/Email/Resend?MemberId=${memberId}`
		: `/api/Email/Resend`;
	return (
		await getAxiosInstance(apiEnvironment).post(
			url,
			{},
			{
				headers: getHeaders(ssId, apiToken),
			}
		)
	).data;
};

/**
 * Verify email
 * @param {object} queryKey
 * @returns {Promise<object>}
 * @endpoint /api/Email/Verify
 */
export const verifyEmail = ({ queryKey }) => {
	const [apiEnvironment, token] = queryKey;
	return getAxiosInstance(apiEnvironment).get('/api/Email/Verify', {
		headers: {
			AshToken: `${token}`,
		},
	});
};

/**
 * Submit change security question
 * @param {string} apiEnvironment
 * @param {string} ssId
 * @param {object} data
 * @returns {Promise<object>}
 * @endpoint /api/V3/accounts/changeSecurityQuestion
 */
export const submitChangeSecurityQuestion = async (
	apiEnvironment,
	ssId,
	data
) =>
	getAxiosInstance(apiEnvironment).post(
		'/api/V3/accounts/changeSecurityQuestion',
		data,
		{
			headers: getHeaders(ssId),
		}
	);

/**
 * Send MFA OTP
 * @param {object} queryKey
 * @returns {Promise<object>}
 * @endpoint /api/Verification/SendOtp
 */
export const sendMFAOtp = async ({ queryKey }) => {
	const [, apiEnvironment, ssId, mfaToken, medium] = queryKey;

	return (
		await getAxiosInstance(apiEnvironment, ssId).get(
			'/api/Verification/SendOtp',
			{
				headers: { 'Content-Type': 'application/json' },
				params: {
					token: mfaToken,
					medium,
				},
			}
		)
	).data;
};

/**
 * Send enforce MFA OTP
 * @param {object} queryKey
 * @returns {Promise<object>}
 * @endpoint /api/Enforce/SendOtp
 */
export const sendEnforceMFAOtp = async ({ queryKey }) => {
	const [, apiEnvironment, mfaToken, medium] = queryKey;

	return (
		await getAxiosInstance(apiEnvironment).get('/api/Enforce/SendOtp', {
			headers: { 'Content-Type': 'application/json' },
			params: {
				token: mfaToken,
				medium,
			},
		})
	).data;
};

/**
 * Validate OTP
 * @param {string} apiEnvironment
 * @param {object} data
 * @returns {Promise<object>}
 * @endpoint /api/Enforce/ValidateOtp
 */
export const validateOtp = async (apiEnvironment, data) =>
	getAxiosInstance(apiEnvironment).post('/api/Enforce/ValidateOtp', data, {
		headers: {
			'Content-Type': 'application/json',
		},
	});

/**
 * Post login validate OTP
 * @param {string} apiEnvironment
 * @param {string} ssId
 * @param {object} data
 * @returns {Promise<object>}
 * @endpoint /api/Verification/ValidateOtp
 */
export const postLoginValidateOtp = async (apiEnvironment, ssId, data) =>
	getAxiosInstance(apiEnvironment, ssId).post(
		'/api/Verification/ValidateOtp',
		data,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

/**
 * Enforce MFA OTP
 * @param {object} queryKey
 * @returns {Promise<object>}
 * @endpoint /api/Enforce/MfaOptions
 */
export const enforceMFAOtp = async ({ queryKey }) => {
	const [, apiEnvironment, mfaToken] = queryKey;

	const response = await getAxiosInstance(apiEnvironment)
		.get('/api/Enforce/MfaOptions', {
			params: {
				token: mfaToken,
			},
		})
		.catch(error => error);

	return response.name === 'AxiosError' ? response : response.data;
};
